import './index.css'
import {
  parseTestMode,
  sanitizeParam,
  showBrandLogo,
  hideBrandLogo,
  applyTestModeBadge,
  applyBranding
} from '../shared/branding.js'
const {
  isFlowsPreview,
  postFlowsEvent,
  observeFlowsSize
} = require('../shared/flows-preview.js')

const API_URL = 'https://api.payhook.link'
const PERIODS = ['weekly', 'monthly']
const PERIOD_LABELS = { weekly: 'Last 7 days', monthly: 'Last 30 days' }

const rootEl = document.getElementById('usage-root')
const footerEl = document.getElementById('usage-footer')
const brandEl = document.getElementById('usage-brand')
const brandNameEl = document.getElementById('usage-brand-name')
const testBadgeEl = document.getElementById('usage-test-badge')
const ringEl = document.getElementById('usage-ring')
const ringValueEl = document.getElementById('usage-ring-value')
const ringUnitEl = document.getElementById('usage-ring-unit')
const titleEl = document.getElementById('usage-title')
const leadEl = document.getElementById('usage-lead')
const periodsEl = document.getElementById('usage-periods')
const resetEl = document.getElementById('usage-reset')
const upgradeEl = document.getElementById('usage-upgrade')
// `?preview=flows`: every state of this page is already URL-driven (see
// README), and branding is fetched with purpose=branding. Preview only stops
// the Upgrade link from navigating the frame and reports it instead.
const flowsPreview = isFlowsPreview()
let usageHit = false

function numericParam (params, name) {
  const value = Number(params.get(name))
  return Number.isFinite(value) && value >= 0 ? value : null
}

function formatAmount (value, unit) {
  const amount = Math.max(0, Math.round(value))
  if (unit === 'minute') return `${amount} min`
  return `${amount} ${amount === 1 ? unit : `${unit}s`}`
}

// Rolling windows have no billing-style reset day, so the client sends the
// exact instant its oldest usage ages out — show that date rather than
// leaving "as each window passes" as the only answer.
function applyResetDate (value) {
  const resetAt = value ? new Date(value) : null
  if (!resetAt || Number.isNaN(resetAt.getTime())) {
    resetEl.hidden = true
    return
  }
  const date = resetAt.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
  const time = resetAt.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  })
  resetEl.textContent = `Free time comes back on ${date} at ${time}`
  resetEl.hidden = false
}

function usageSnapshot (params) {
  return PERIODS.map((period) => ({
    period,
    used: numericParam(params, `${period}Used`),
    limit: numericParam(params, `${period}Limit`)
  })).filter(({ used, limit }) => used != null && limit != null && limit > 0)
}

function applyUsage (periods, unit) {
  const constrained = periods.reduce((closest, period) => {
    if (!closest) return period
    return period.limit - period.used < closest.limit - closest.used ? period : closest
  }, null)
  const remaining = constrained ? Math.max(0, constrained.limit - constrained.used) : 0
  const hit = periods.some(({ used, limit }) => used >= limit)
  usageHit = hit
  const constrainedRatio = constrained ? Math.min(1, constrained.used / constrained.limit) : 0

  ringEl.style.setProperty('--progress', `${Math.round(constrainedRatio * 360)}deg`)
  ringValueEl.textContent = String(Math.round(remaining))
  ringUnitEl.textContent = unit === 'minute' ? 'min left' : `${unit}s left`
  titleEl.textContent = hit ? 'Free time used' : 'Your Slowed time'
  leadEl.textContent = hit
    ? 'You reached one of your free usage limits. Unlock Pro to keep listening without limits.'
    : 'Your free time refreshes automatically as each rolling window passes.'

  periodsEl.innerHTML = periods.map(({ period, used, limit }) => {
    const ratio = Math.min(100, Math.round((used / limit) * 100))
    return `
      <div class="usage__period">
        <div class="usage__period-head">
          <strong>${PERIOD_LABELS[period]}</strong>
          <span>${formatAmount(used, unit)} of ${formatAmount(limit, unit)}</span>
        </div>
        <div class="usage__track" role="progressbar" aria-label="${PERIOD_LABELS[period]} usage" aria-valuemin="0" aria-valuemax="${limit}" aria-valuenow="${used}">
          <span class="usage__fill" style="width:${ratio}%"></span>
        </div>
      </div>
    `
  }).join('')
}

async function applyRemoteBranding ({ paymentHookId, testMode }) {
  try {
    const url = new URL(`${API_URL}/unlock`)
    url.searchParams.set('paymentHookId', paymentHookId)
    url.searchParams.set('purpose', 'branding')
    const headers = testMode ? { 'Test-Mode': 'True' } : {}
    const response = await fetch(url, { headers })
    if (!response.ok) return
    const { branding, labels } = await response.json()
    applyBranding(branding, {
      rootEl,
      accentVarName: '--usage-accent',
      accentSoftVarName: '--usage-accent-soft',
      hasLogoClass: 'usage--has-logo',
      applyLogo: (value) => showBrandLogo(value, {
        brandEl,
        brandNameEl,
        logoClass: 'usage__brand-logo'
      }),
      applyNoLogo: (value) => hideBrandLogo(value, {
        brandEl,
        brandNameEl,
        logoClass: 'usage__brand-logo'
      }),
      extra: (value) => {
        const productName = String(value.extensionName || '').replace(/\s+pro$/i, '').trim()
        if (productName && !usageHit) titleEl.textContent = `Your ${productName} time`
      }
    })
    if (labels?.upgrade) upgradeEl.textContent = labels.upgrade
  } catch (_) {}
}

async function init () {
  const params = new URLSearchParams(window.location.search)
  const paymentHookId = sanitizeParam(params.get('paymentHookId'))
  const extensionId = sanitizeParam(params.get('extensionId'))
  const version = sanitizeParam(params.get('version'))
  const email = sanitizeParam(params.get('email'))
  const testMode = parseTestMode(sanitizeParam(params.get('testMode')))
  const unit = sanitizeParam(params.get('unit')) || 'use'
  const periods = usageSnapshot(params)

  applyTestModeBadge(rootEl, testBadgeEl, testMode, 'usage--test-mode')
  observeFlowsSize(rootEl, { include: [footerEl] })
  applyUsage(periods, unit)
  applyResetDate(sanitizeParam(params.get('resetAt')))

  const unlockUrl = new URL('/', window.location.origin)
  if (paymentHookId) unlockUrl.searchParams.set('paymentHookId', paymentHookId)
  if (extensionId) unlockUrl.searchParams.set('extensionId', extensionId)
  if (version) unlockUrl.searchParams.set('version', version)
  if (testMode) unlockUrl.searchParams.set('testMode', 'true')
  // The unlock page prefills its email field from this.
  if (email) unlockUrl.searchParams.set('email', email)
  upgradeEl.href = unlockUrl.href
  if (flowsPreview) {
    upgradeEl.addEventListener('click', (event) => {
      event.preventDefault()
      postFlowsEvent('upgrade-click', { href: upgradeEl.href })
    })
  }

  if (paymentHookId) await applyRemoteBranding({ paymentHookId, testMode })
  rootEl.hidden = false
  footerEl.hidden = false
}

init()
